import React from 'react';

import { IMilestoneFields } from '../../../types/contentful';
import { Block, BLOCKS, Inline } from '@contentful/rich-text-types';

import { getImageData } from '../../utils/contentful';

import RichText from '../RichText';
import cx from 'classnames';

import { StyledImage, ImageColumn } from './styles';
import ImageLink from '../ImageLink';
import { normalizeHexCode } from '~/styles/utils';
import { RenderNode } from '@contentful/rich-text-react-renderer';

interface Props extends IMilestoneFields {
  sysId?: string;
}

export function Milestone({
  headingText,
  image,
  imageLink,
  bodyCopy,
  disclaimer,
  backgroundColor,
  textColor,
  sysId,
  imageFirst,
  anchorLink
}: Props) {
  const RICH_TEXT_OVERRIDE: RenderNode = {
    [BLOCKS.PARAGRAPH]: (_node: Block | Inline, children) => {
      return (
        <p className={`body-small text-${normalizeHexCode(textColor, true)}`}>
          {children}
        </p>
      );
    }
  };

  return (
    <div
      className="block-container py-container"
      id={anchorLink ?? `${sysId}`}
      data-blockid={`milestone-${sysId}`}
      data-testid="milestone-block"
    >
      <div
        className={cx(
          'layout flex-col-reverse items-center rounded-3xl !p-0 desktop:rounded-4xl',
          {
            'tablet:flex-row-reverse': imageFirst,
            'tablet:flex-row': !imageFirst
          }
        )}
        style={{
          backgroundColor: normalizeHexCode(backgroundColor) ?? 'transparent'
        }}
      >
        <div
          className={cx(
            'column w-10/12 flex-col items-center justify-center py-12 tablet:w-1/2 desktop:py-16',
            {
              'px-0 tablet:pl-8': imageFirst,
              'px-0 tablet:pr-8': !imageFirst
            }
          )}
        >
          <div className="max-w-[480px]">
            <div className="mb-3">
              <h2
                className={`heading-2 text-${normalizeHexCode(
                  textColor,
                  true
                )}`}
              >
                {headingText}
              </h2>
            </div>
            {bodyCopy && (
              <div className={disclaimer ? 'border-box mb-6' : 'border-box'}>
                <RichText
                  bulletColor={textColor}
                  data={bodyCopy}
                  overrideOptions={RICH_TEXT_OVERRIDE}
                />
              </div>
            )}
            {disclaimer && (
              <p
                className={`body-extra-small body-small whitespace-pre-wrap text-${normalizeHexCode(
                  textColor,
                  true
                )}`}
              >
                {disclaimer}
              </p>
            )}
          </div>
        </div>
        <ImageColumn
          size={{ 0: 12, tablet: 5 }}
          color={backgroundColor || ''}
          borderRadius={{ 0: 'block', tablet: '0' }}
          alignSelf="flex-end"
          imageFirst={!!imageFirst}
        >
          <div>
            {imageLink ? (
              <ImageLink
                {...{
                  ...imageLink.fields
                }}
              >
                {(image0) => <StyledImage {...getImageData(image0)} />}
              </ImageLink>
            ) : (
              <StyledImage {...getImageData(image)} />
            )}
          </div>
        </ImageColumn>
      </div>
    </div>
  );
}
